/**
 * Parses message content to find which citation numbers are actually used, in order.
 *
 * Extracts citation indices from markdown content (e.g., [1], [2], [10]) in order
 * of first appearance. This order is used to create sequential display mappings.
 * Used to filter and renumber citations at the bottom of chat messages.
 *
 * Also handles malformed LLM output:
 * - Combined: [1, 3] or [1,3] → extracts 1, 3
 * - Annotated: [1 EMP] → extracts 1
 *
 * @param content - The markdown message content
 * @returns Array of 1-based citation indices in order of first appearance
 *
 * @example
 * parseUsedCitations("Text [1] and [3]")     // [1, 3]
 * parseUsedCitations("[3] and [1] and [3]")  // [3, 1] (order of first appearance, no duplicates)
 * parseUsedCitations("Text [1, 3] here")     // [1, 3]
 * parseUsedCitations("Text [1 EMP] here")    // [1]
 * parseUsedCitations("No citations")         // []
 */
export function parseUsedCitations(content: string): number[] {
  const seenIndices = new Set<number>()
  const orderedIndices: number[] = []

  function addIndex(index: number) {
    if (index >= 1 && !seenIndices.has(index)) {
      seenIndices.add(index)
      orderedIndices.push(index)
    }
  }

  // Standard individual citations: [1], [2], [10]
  const standardRegex = /\[(\d+)\]/g
  let match
  while ((match = standardRegex.exec(content)) !== null) {
    addIndex(parseInt(match[1]!, 10))
  }

  // Comma-separated: [1, 3] or [1,3,7]
  const combinedRegex = /\[(\d+(?:\s*,\s*\d+)+)\]/g
  while ((match = combinedRegex.exec(content)) !== null) {
    const nums = match[1]!.split(/\s*,\s*/)
    for (const num of nums) {
      addIndex(parseInt(num, 10))
    }
  }

  // Annotated: [1 EMP] or [1 Document Name] (digit followed by space and text)
  const annotatedRegex = /\[(\d+)\s+[^\]]+\]/g
  while ((match = annotatedRegex.exec(content)) !== null) {
    addIndex(parseInt(match[1]!, 10))
  }

  return orderedIndices
}

/**
 * Parses message content to find which library citation numbers are used, in order.
 *
 * Extracts library citation indices from markdown content (e.g., [L1], [L2], [L10]) in order
 * of first appearance. Used to filter and display library citations at the bottom of chat messages.
 *
 * Also handles malformed LLM output:
 * - Combined: [L1, L3] or [L1,L3] → extracts 1, 3
 * - Annotated: [L1 EMP] → extracts 1
 *
 * @param content - The markdown message content
 * @returns Array of 1-based library citation indices in order of first appearance
 *
 * @example
 * parseUsedLibraryCitations("Text [L1] and [L3]")     // [1, 3]
 * parseUsedLibraryCitations("[L3] and [L1] and [L3]")  // [3, 1] (order of first appearance, no duplicates)
 * parseUsedLibraryCitations("Text [L1, L3] here")      // [1, 3]
 * parseUsedLibraryCitations("Text [L1 EMP] here")      // [1]
 * parseUsedLibraryCitations("No citations")             // []
 */
export function parseUsedLibraryCitations(content: string): number[] {
  const seenIndices = new Set<number>()
  const orderedIndices: number[] = []

  function addIndex(index: number) {
    if (index >= 1 && !seenIndices.has(index)) {
      seenIndices.add(index)
      orderedIndices.push(index)
    }
  }

  // Standard individual: [L1], [L2], [L10]
  const standardRegex = /\[L(\d+)\]/g
  let match
  while ((match = standardRegex.exec(content)) !== null) {
    addIndex(parseInt(match[1]!, 10))
  }

  // Comma-separated: [L1, L3] or [L1,L3] or mixed [L1, 3]
  const combinedRegex = /\[(L\d+(?:\s*,\s*L?\d+)+)\]/g
  while ((match = combinedRegex.exec(content)) !== null) {
    const parts = match[1]!.split(/\s*,\s*/)
    for (const part of parts) {
      const num = part.replace(/^L/, '')
      addIndex(parseInt(num, 10))
    }
  }

  // Annotated: [L1 EMP] or [L1 Environmental Management Plan]
  const annotatedRegex = /\[L(\d+)\s+[^\]]+\]/g
  while ((match = annotatedRegex.exec(content)) !== null) {
    addIndex(parseInt(match[1]!, 10))
  }

  return orderedIndices
}
